import React, { useEffect, useLayoutEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import Layout from '../components/layouts';

import heroEn from '../images/futur-call-hero-en.jpg';
import heroEs from '../images/futur-call-hero-es.jpg';
import gallery1 from '../images/futur-call-1.jpg';
import gallery2 from '../images/futur-call-2.jpg';
import gallery3 from '../images/futur-call-3.jpg';
import gallery4 from '../images/futur-call-4.jpg';

type Lang = 'en' | 'es';

const AIRTABLE_FORMS: Record<Lang, string> = {
  en: 'https://airtable.com/embed/appj3blBRUlgymmDw/pagTds3loJP5LcH1t/form',
  es: 'https://airtable.com/embed/appj3blBRUlgymmDw/pagVIZbZhds2YIK4I/form',
};
const LANG_STORAGE_KEY = 'futur-call-lang';

type Section = {
  id: string;
  title: string;
  paragraphs?: string[];
  items?: string[];
  after?: string[];
};

const gallerySrc = [gallery1, gallery2, gallery3, gallery4];

const copy: Record<Lang, {
  pageTitle: string;
  meta: string;
  heroTitle: string;
  heroSrc: string;
  guidelinesHeading: string;
  intro: string[];
  sections: Section[];
  applyLabel: string;
  formHeading: string;
  formTitle: string;
  galleryHeading: string;
  galleryAlts: string[];
  langLabel: string;
  termsLink: string;
}> = {
  en: {
    pageTitle: 'Call to Artists | FUTURFestival Mexico x Global Inheritance',
    meta: 'International open call 2026: FUTURFestival Mexico x TRASHed. Transform a recycling bin into art at Parque Fundidora, Monterrey, November 13–14.',
    heroTitle: 'FUTUR × TRASHed. 13–14 November, Fundidora Monterrey, Mexico 2026. Apply to paint a bin for FUTURFestival Mexico.',
    heroSrc: heroEn,
    guidelinesHeading: 'Guidelines',
    intro: [
      'Call to Artists 2026 — FUTURFestival Mexico and Global Inheritance invite artists and collectives to submit bin concepts for Art of Recycling at FUTURFestival Mexico. Selected artists will receive VIP passes, an art budget and be part of the festival experience at Parque Fundidora in Monterrey, MX on November 13th and 14th, 2026.',
      'Submit your concepts by October 27, 2026 at 5:00 PM PT. Selected artists will be announced on October 29, 2026. Apply by filling out the TRASHed Artist Form, and post your concept design on social media using #TRASHedxFutur and tagging @globalinheritance and @futurfestivalmexico on Instagram.',
    ],
    sections: [
      {
        id: 'submissions',
        title: 'Submissions',
        paragraphs: [
          'Submit your bin concept (2 MB or smaller) by October 27, 2026, at 5:00 PM PT. There is no limit to the number of concepts you can submit. AI-generated art will not be accepted. Selected artists will be announced on October 29, 2026.',
        ],
      },
      {
        id: 'benefits',
        title: 'Benefits',
        paragraphs: ['Selected participants will receive the following benefits:'],
        items: [
          'Two VIP Premium tickets, valid for both days of the festival (November 13 and 14, 2026).',
          'Four 50% discount codes for the artist’s friends and family.',
          'A stipend of $1,500 MXN per participant for the purchase of art materials.',
          'For participants in the live painting format (Bin Battle): meals and hydration during their painting session at the festival.',
        ],
      },
      {
        id: 'dates',
        title: 'Key Dates',
        items: [
          'October 13, 2026 — Open call opens.',
          'October 27, 2026 — Open call closes.',
          'October 29, 2026 — Selected artists announced.',
          'October 30 – November 1, 2026 — Blank bins delivered to participants in the pre-painted format.',
          'November 10, 2026 — Deadline to deliver completed bins.',
          'November 14, 2026 — Bin Battle (live painting) and exhibition of completed pieces at FUTURFestival Mexico.',
        ],
      },
      {
        id: 'guidelines',
        title: 'General Guidelines',
        paragraphs: [
          'Participants agree that, following the festival, their completed recycling bins will be donated to elementary schools in Monterrey, continuing Global Inheritance’s tradition from previous editions. Artists will not be entitled to request the return of their pieces.',
          'By participating, artists grant Global Inheritance and FUTURFestival Mexico permission to photograph and film their artwork and use these images and videos to promote the festival and participating artists through press, social media, and other official communication channels.',
          'Since the completed bins will be donated to schools, all designs must be appropriate for a general audience and free of violent, sexual, or discriminatory content, as well as content promoting illegal substances.',
          'Global Inheritance and FUTURFestival Mexico reserve the right to reject or request modifications to any proposal that does not meet these guidelines at any stage of the process.',
        ],
      },
    ],
    applyLabel: 'Apply here',
    formHeading: 'Complete the form',
    formTitle: 'Art of Recycling FUTURFestival Mexico application',
    galleryHeading: 'Gallery',
    galleryAlts: [
      'Painted bins lined along a reflecting pool at an industrial site',
      'Yellow bin painted with a creature drawing',
      'A row of painted bins beneath a large mural',
      'Painted bins in front of a yellow Fundidora locomotive',
    ],
    langLabel: 'Language',
    termsLink: 'Terms & Conditions',
  },
  es: {
    pageTitle: 'Convocatoria a artistas | FUTURFestival Mexico x Global Inheritance',
    meta: 'Convocatoria internacional 2026: FUTURFestival Mexico x TRASHed. Transforma un bote de reciclaje en arte en el Parque Fundidora, Monterrey, 13 y 14 de noviembre.',
    heroTitle: 'FUTUR × TRASHed. 13 y 14 de noviembre, Fundidora Monterrey, México 2026. Aplica para pintar un bote para FUTURFestival Mexico.',
    heroSrc: heroEs,
    guidelinesHeading: 'CONVOCATORIA INTERNACIONAL 2026 — FUTURFestival Mexico x TRASHed.',
    intro: [
      'Convocatoria para Artistas 2026— FUTURFestival Mexico y Global Inheritance invitan a artistas y colectivos a presentar sus propuestas para intervenir botes de reciclaje como parte del programa Art of Recycling en FUTURFestival Mexico. Los artistas seleccionados recibirán accesos VIP, un presupuesto para materiales artísticos y la oportunidad de formar parte de la experiencia del festival, que se llevará a cabo los días 13 y 14 de noviembre de 2026 en el Parque Fundidora de Monterrey, México.',
      'Envía tu propuesta antes del 27 de octubre de 2026 a las 5:00 p.m. PT. Las y los artistas seleccionados se anunciarán el 29 de octubre de 2026. Aplica llenando el formulario de artista TRASHed y publica tu diseño en redes con #TRASHedxFutur, etiquetando a @globalinheritance y @futurfestivalmexico en Instagram.',
    ],
    sections: [
      {
        id: 'submissions',
        title: 'Cómo participar',
        paragraphs: [
          'Envía el concepto de tu bote (2 MB o menos) antes del 27 de octubre de 2026 a las 5:00 p.m. PT. No hay límite de conceptos que puedas enviar. No se aceptará arte generado por IA. Las y los artistas seleccionados se anunciarán el 29 de octubre de 2026.',
        ],
      },
      {
        id: 'benefits',
        title: 'Beneficios',
        paragraphs: ['Las y los participantes seleccionados recibirán los siguientes beneficios:'],
        items: [
          'Dos boletos VIP Premium, válidos para ambos días del festival (13 y 14 de noviembre de 2026).',
          'Cuatro códigos de descuento del 50% para amistades y familiares del artista.',
          'Un apoyo de $1,500 MXN para materiales artísticos.',
          'Para participantes en el formato de pintura en vivo (Bin Battle): alimentos e hidratación durante su sesión de pintura en el festival.',
        ],
      },
      {
        id: 'dates',
        title: 'Fechas clave',
        items: [
          '13 de octubre de 2026 — Abre la convocatoria.',
          '27 de octubre de 2026 — Cierra la convocatoria.',
          '29 de octubre de 2026 — Se anuncian las y los artistas seleccionados.',
          '30 de octubre – 1 de noviembre de 2026 — Entrega de botes en blanco a participantes del formato pre-pintado.',
          '10 de noviembre de 2026 — Fecha límite para entregar los botes terminados.',
          '14 de noviembre de 2026 — Bin Battle (pintura en vivo) y exhibición de las piezas terminadas en FUTURFestival Mexico.',
        ],
      },
      {
        id: 'guidelines',
        title: 'Lineamientos generales',
        paragraphs: [
          'Las y los participantes aceptan que, después del festival, sus botes de reciclaje terminados serán donados a escuelas primarias de Monterrey, continuando la tradición de Global Inheritance de ediciones anteriores. Las y los artistas no tendrán derecho a solicitar la devolución de sus piezas.',
          'Al participar, las y los artistas otorgan a Global Inheritance y a FUTURFestival Mexico permiso para fotografiar y filmar su obra y usar esas imágenes y videos para promover el festival y a las y los artistas participantes a través de prensa, redes sociales y otros canales oficiales de comunicación.',
          'Dado que los botes terminados se donarán a escuelas, todos los diseños deben ser apropiados para un público general y estar libres de contenido violento, sexual o discriminatorio, así como de contenido que promueva sustancias ilegales.',
          'Global Inheritance y FUTURFestival Mexico se reservan el derecho de rechazar o solicitar modificaciones a cualquier propuesta que no cumpla estos lineamientos en cualquier etapa del proceso.',
        ],
      },
    ],
    applyLabel: 'Aplica aquí',
    formHeading: 'Completa el formulario',
    formTitle: 'Solicitud Art of Recycling FUTURFestival Mexico',
    galleryHeading: 'Galería',
    galleryAlts: [
      'Botes pintados a lo largo de un espejo de agua en un sitio industrial',
      'Bote amarillo pintado con el dibujo de una criatura',
      'Una fila de botes pintados bajo un mural',
      'Botes pintados frente a una locomotora amarilla de Fundidora',
    ],
    langLabel: 'Idioma',
    termsLink: 'Términos y condiciones',
  },
};

const useIsoLayoutEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect;

function preferredLang(): Lang {
  if (typeof window === 'undefined') return 'en';
  try {
    const saved = window.localStorage.getItem(LANG_STORAGE_KEY);
    if (saved === 'en' || saved === 'es') return saved;
  } catch (err) {
    // Private browsing can block storage; fall through to the browser language.
  }
  const list = (navigator.languages && navigator.languages.length
    ? navigator.languages
    : [navigator.language]
  ).filter(Boolean);
  return list.some((code) => code.toLowerCase().startsWith('es')) ? 'es' : 'en';
}

const FuturCallToArtists = () => {
  const [lang, setLang] = useState<Lang | null>(null);
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const activeLang: Lang = lang || 'en';
  const text = copy[activeLang];

  useIsoLayoutEffect(() => {
    setLang(preferredLang());
  }, []);

  const chooseLang = (next: Lang) => {
    setLang(next);
    try {
      window.localStorage.setItem(LANG_STORAGE_KEY, next);
    } catch (err) {
      // Ignore storage failures; the in-page choice still applies.
    }
  };

  const toggle = (id: string) => {
    setOpen((current) => ({ ...current, [id]: !current[id] }));
  };

  return (
    <Layout navBg>
      <Helmet htmlAttributes={{ lang: activeLang }}>
        <title>{text.pageTitle}</title>
        <meta name="description" content={text.meta} />
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
        <link
          href="https://fonts.googleapis.com/css2?family=Funnel+Sans:wght@300;400;500;600;700;800&display=swap"
          rel="stylesheet"
        />
        <link href="https://fonts.cdnfonts.com/css/sweet-sans-pro" rel="stylesheet" />
      </Helmet>

      <div className="futur-call-page">
        <div className="futur-call-lang-bar">
          <div className="futur-call-lang" role="group" aria-label={text.langLabel}>
            <button type="button" aria-pressed={lang === 'en'} onClick={() => chooseLang('en')}>
              EN
            </button>
            <button type="button" aria-pressed={lang === 'es'} onClick={() => chooseLang('es')}>
              ES
            </button>
          </div>
        </div>

        <header className="futur-call-hero">
          <h1 className="futur-call-hero-title">{text.heroTitle}</h1>
          {lang ? (
            <img src={text.heroSrc} alt="" />
          ) : (
            <div className="futur-call-hero-placeholder" aria-hidden="true" />
          )}
        </header>

        <section className="futur-call-body">
          <div className="futur-call-columns">
            <div className="futur-call-guidelines">
              <h2>{text.guidelinesHeading}</h2>
              {text.intro.map((paragraph) => (
                <p key={paragraph}>{paragraph}</p>
              ))}

              <div className="futur-call-accordion">
                {text.sections.map((item) => {
                  const isOpen = !!open[item.id];
                  return (
                    <div key={item.id} className={`futur-call-accordion-item${isOpen ? ' is-open' : ''}`}>
                      <button
                        type="button"
                        className="futur-call-accordion-trigger"
                        aria-expanded={isOpen}
                        onClick={() => toggle(item.id)}
                      >
                        <span>{item.title}</span>
                        <span className="futur-call-accordion-chevron" aria-hidden="true" />
                      </button>
                      {isOpen && (
                        <div className="futur-call-accordion-panel">
                          {item.paragraphs && item.paragraphs.map((paragraph) => (
                            <p key={paragraph}>{paragraph}</p>
                          ))}
                          {item.items && (
                            <ul>
                              {item.items.map((entry) => (
                                <li key={entry}>{entry}</li>
                              ))}
                            </ul>
                          )}
                          {item.after && item.after.map((paragraph) => (
                            <p key={paragraph}>{paragraph}</p>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            <aside className="futur-call-form-panel" id="form">
              <p className="futur-call-apply-label">{text.applyLabel}</p>
              <h2>{text.formHeading}</h2>
              <iframe
                key={activeLang}
                className="futur-call-airtable"
                title={text.formTitle}
                src={AIRTABLE_FORMS[activeLang]}
                frameBorder="0"
                width="100%"
              />
              <p className="futur-call-terms-link">
                <a href="/futur-call-terms">{text.termsLink}</a>
              </p>
            </aside>
          </div>
        </section>

        <section className="futur-call-gallery">
          <h2>{text.galleryHeading}</h2>
          <div className="futur-call-gallery-grid">
            {gallerySrc.map((src, index) => (
              <div key={text.galleryAlts[index]} className="futur-call-gallery-tile">
                <img src={src} alt={text.galleryAlts[index]} />
              </div>
            ))}
          </div>
        </section>
      </div>
    </Layout>
  );
};

export default FuturCallToArtists;
